import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function MatchFormDialog({
  open,
  onClose,
  onSubmit,
  initialData,
  loading,
  categories,
  teams,
  venues,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [categoryId, setCategoryId] = useState("");
  const [teamLocalId, setTeamLocalId] = useState("");
  const [teamVisitorId, setTeamVisitorId] = useState("");
  const [venueId, setVenueId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [status, setStatus] = useState("scheduled");
  const [scoreLocal, setScoreLocal] = useState("");
  const [scoreVisitor, setScoreVisitor] = useState("");

  useEffect(() => {
    if (initialData) {
      setCategoryId(
        initialData.category_id ? String(initialData.category_id) : ""
      );
      setTeamLocalId(
        initialData.team_local_id
          ? String(initialData.team_local_id)
          : ""
      );
      setTeamVisitorId(
        initialData.team_visitor_id
          ? String(initialData.team_visitor_id)
          : ""
      );
      setVenueId(initialData.venue_id ? String(initialData.venue_id) : "");
      setDate(initialData.date || "");
      setTime(String(initialData.time || "").slice(0, 5));
      setStatus(initialData.status || "scheduled");
      setScoreLocal(
        initialData.score_local === null ||
          initialData.score_local === undefined
          ? ""
          : String(initialData.score_local)
      );
      setScoreVisitor(
        initialData.score_visitor === null ||
          initialData.score_visitor === undefined
          ? ""
          : String(initialData.score_visitor)
      );
    } else {
      const firstCategoryId = categories[0] ? String(categories[0].id) : "";
      const categoryTeams = teams.filter(
        (team) => String(team.category_id) === firstCategoryId
      );

      setCategoryId(firstCategoryId);
      setTeamLocalId(categoryTeams[0] ? String(categoryTeams[0].id) : "");
      setTeamVisitorId(categoryTeams[1] ? String(categoryTeams[1].id) : "");
      setVenueId(venues[0] ? String(venues[0].id) : "");
      setDate("");
      setTime("");
      setStatus("scheduled");
      setScoreLocal("");
      setScoreVisitor("");
    }
  }, [initialData, open, categories, teams, venues]);

  const filteredTeams = useMemo(() => {
    return teams.filter(
      (team) => String(team.category_id) === String(categoryId)
    );
  }, [teams, categoryId]);

  useEffect(() => {
    if (!categoryId) return;

    const currentIds = filteredTeams.map((team) => String(team.id));

    if (!currentIds.includes(String(teamLocalId))) {
      setTeamLocalId(filteredTeams[0] ? String(filteredTeams[0].id) : "");
    }

    if (!currentIds.includes(String(teamVisitorId))) {
      setTeamVisitorId(filteredTeams[1] ? String(filteredTeams[1].id) : "");
    }
  }, [categoryId, filteredTeams, teamLocalId, teamVisitorId]);

  const handleSave = () => {
    onSubmit({
      category_id: categoryId,
      team_local_id: teamLocalId,
      team_visitor_id: teamVisitorId,
      venue_id: venueId,
      date,
      time,
      status,
      score_local: scoreLocal === "" ? null : Number(scoreLocal),
      score_visitor: scoreVisitor === "" ? null : Number(scoreVisitor),
    });
  };

  const canSave =
    categoryId &&
    teamLocalId &&
    teamVisitorId &&
    venueId &&
    date &&
    time &&
    teamLocalId !== teamVisitorId;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: "18px",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "primary.main",
          color: "#fff",
          fontWeight: 700,
          fontSize: "1.05rem",
          mb: 3,
        }}
      >
        {initialData ? "Editar partido" : "Nuevo partido"}
      </DialogTitle>

      <DialogContent sx={{ pt: 2.2 }}>
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            label="Categoría"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            {categories.map((category) => (
              <MenuItem
                key={category.id}
                value={String(category.id)}
              >
                {category.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Equipo local"
            value={teamLocalId}
            onChange={(e) => setTeamLocalId(e.target.value)}
          >
            {filteredTeams.map((team) => (
              <MenuItem key={team.id} value={String(team.id)}>
                {team.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Equipo visitante"
            value={teamVisitorId}
            onChange={(e) => setTeamVisitorId(e.target.value)}
          >
            {filteredTeams.map((team) => (
              <MenuItem key={team.id} value={String(team.id)}>
                {team.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Sede"
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
          >
            {venues.map((venue) => (
              <MenuItem key={venue.id} value={String(venue.id)}>
                {venue.name}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              type="date"
              label="Fecha"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              type="time"
              label="Hora"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <TextField
            select
            fullWidth
            label="Estado"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="scheduled">Programado</MenuItem>
            <MenuItem value="finished">Finalizado</MenuItem>
            <MenuItem value="cancelled">Cancelado</MenuItem>
          </TextField>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              type="number"
              label="Marcador local"
              value={scoreLocal}
              onChange={(e) => setScoreLocal(e.target.value)}
              inputProps={{ min: 0 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Marcador visitante"
              value={scoreVisitor}
              onChange={(e) => setScoreVisitor(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.4,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !canSave}
        >
          {loading
            ? "Guardando..."
            : initialData
            ? "Actualizar"
            : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}